import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { assets } from '../../assets/assets';
import './ResetPasswordModal.css';

const ResetPasswordModal = ({ setShowResetPassword, setShowLogin }) => {
    const [step, setStep] = useState('request'); // 'request' or 'reset'
    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [token, setToken] = useState('');
    const [tokenValid, setTokenValid] = useState(false);

    // Dynamic URL based on environment
    const getApiUrl = () => {
        const hostname = window.location.hostname;
        const isDevelopment = hostname === 'localhost' || hostname === '127.0.0.1';
        
        if (isDevelopment) {
            // Use the proxy in development to avoid CORS issues
            return '';
        } else {
            // Remove trailing slash if present to avoid double slashes
            const backendUrl = (import.meta.env.VITE_BACKEND_URL || 'https://dashbasket.onrender.com');
            return backendUrl.replace(/\/$/, '');
        }
    };
    
    const url = getApiUrl();

    const handleRequestReset = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await axios.post(`${url}/api/user/request-password-reset`, {
                email,
                userType: 'customer'
            });

            if (response.data.success) {
                toast.success(response.data.message);
                setEmail('');
                // Close the modal after successful request
                setShowResetPassword(false);
                setShowLogin(true); // Show login modal again
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Password reset request error:', error);
            toast.error(error.response?.data?.message || 'Failed to send reset email. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters long');
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post(`${url}/api/user/reset-password`, {
                token,
                newPassword,
                userType: 'customer'
            });

            if (response.data.success) {
                toast.success('Password reset successfully! You can now login with your new password.');
                setShowResetPassword(false);
                setShowLogin(true); // Show login modal again
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Password reset error:', error);
            toast.error(error.response?.data?.message || 'Failed to reset password. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        setShowResetPassword(false);
        setShowLogin(true);
    };

    return (
        <div className='reset-password-modal'>
            <div className="reset-password-modal-container">
                <div className="reset-password-modal-title">
                    <h2>{step === 'request' ? 'Reset Password' : 'Set New Password'}</h2>
                    <img onClick={() => setShowResetPassword(false)} src={assets.cross_icon} alt="" />
                </div>

                <div className="reset-password-modal-content">
                    <p className="reset-password-description">
                        {step === 'request' 
                            ? 'Enter your email address and we\'ll send you a link to reset your password.'
                            : 'Enter your new password below.'
                        }
                    </p>

                    {step === 'request' ? (
                        <form onSubmit={handleRequestReset} className="reset-password-form">
                            <div className="form-group">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                />
                            </div>

                            <button 
                                type="submit" 
                                className="reset-password-btn"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Sending...' : 'Send Reset Link'}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleResetPassword} className="reset-password-form">
                            <div className="form-group">
                                <div className="password-input-container">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Enter new password"
                                        required
                                    />
                                    <button 
                                        type="button" 
                                        className="password-toggle-btn"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M3 3l18 18M10.5 10.5a3 3 0 1 0 4.24 4.24m1.06-5.76a3 3 0 0 0-5.3-5.3M12 5c-4.48 0-8.4 2.8-10 7 .6 1.6 1.6 3 2.8 4.1m2.2 1.4c1.6.6 3.3.9 5 .9 4.48 0 8.4-2.8 10-7-.7-1.8-1.8-3.3-3.1-4.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="form-group">
                                <div className="password-input-container">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Confirm new password"
                                        required
                                    />
                                    <button 
                                        type="button" 
                                        className="password-toggle-btn"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M3 3l18 18M10.5 10.5a3 3 0 1 0 4.24 4.24m1.06-5.76a3 3 0 0 0-5.3-5.3M12 5c-4.48 0-8.4 2.8-10 7 .6 1.6 1.6 3 2.8 4.1m2.2 1.4c1.6.6 3.3.9 5 .9 4.48 0 8.4-2.8 10-7-.7-1.8-1.8-3.3-3.1-4.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <button 
                                type="submit" 
                                className="reset-password-btn"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </form>
                    )}

                    <div className="reset-password-actions">
                        <button 
                            type="button"
                            onClick={handleBackToLogin}
                            className="back-to-login-btn"
                        >
                            Back to Login
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordModal; 